package com.daytimegaming.ourdailyfinances.presentation.account

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.daytimegaming.ourdailyfinances.domain.Response
import com.daytimegaming.ourdailyfinances.domain.model.Account
import com.daytimegaming.ourdailyfinances.domain.model.Holding
import com.daytimegaming.ourdailyfinances.domain.model.InvestmentTransaction
import com.daytimegaming.ourdailyfinances.domain.model.SecurityPerformance
import com.daytimegaming.ourdailyfinances.domain.repository.TransactionRepository
import com.daytimegaming.ourdailyfinances.domain.usecase.AccountUseCase
import com.daytimegaming.ourdailyfinances.domain.usecase.DashboardUseCase
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.todayIn

data class SecurityPriceEntry(
    val date: LocalDate,
    val price: Double,
    val quantity: Double,
    val value: Double
)

sealed class SecurityHoldingDetailScreenState {
    data object Loading : SecurityHoldingDetailScreenState()
    data class Error(val message: String) : SecurityHoldingDetailScreenState()
    data class Loaded(
        val account: Account,
        val holding: Holding,
        val transactions: List<InvestmentTransaction>,
        val priceHistory: List<SecurityPriceEntry>,
        val chartPoints: List<Pair<LocalDate, Double>>
    ) : SecurityHoldingDetailScreenState()
}

class SecurityHoldingDetailViewModel(
    private val accountId: String,
    private val securityId: String,
    private val dashboardId: String? = null,
    private val accountUseCase: AccountUseCase,
    private val transactionRepository: TransactionRepository,
    private val dashboardUseCase: DashboardUseCase,
) : ViewModel() {

    private val _state = MutableStateFlow<SecurityHoldingDetailScreenState>(SecurityHoldingDetailScreenState.Loading)
    val state = _state.asStateFlow()

    private val _securityPerformance = MutableStateFlow<SecurityPerformance?>(null)
    private var hasLoadedSecurityPerformance = false

    private fun checkAndLoadSecurityPerformance() {
        if (!hasLoadedSecurityPerformance) {
            hasLoadedSecurityPerformance = true
            viewModelScope.launch {
                val response = try {
                    if (dashboardId != null) {
                        transactionRepository.getSecurityPerformance(dashboardId = dashboardId)
                    } else {
                        transactionRepository.getSecurityPerformance(accountId = accountId)
                    }
                } catch (e: Exception) {
                    Response.Error(e.message ?: "Error loading security performance")
                }
                if (response is Response.Success) {
                    _securityPerformance.value = response.data
                }
            }
        }
    }

    init {
        load()
    }

    fun load() {
        viewModelScope.launch {
            transactionRepository.refresh()
        }
        checkAndLoadSecurityPerformance()

        if (dashboardId != null) {
            viewModelScope.launch {
                combine(
                    dashboardUseCase.GetDashboardDetail(dashboardId),
                    transactionRepository.investments,
                    _securityPerformance
                ) { dashboardResponse, investmentsResponse, securityPerformance ->
                    when (dashboardResponse) {
                        is Response.Loading -> {
                            SecurityHoldingDetailScreenState.Loading
                        }

                        is Response.Error -> {
                            SecurityHoldingDetailScreenState.Error(dashboardResponse.message)
                        }

                        is Response.Success -> {
                            val detail = dashboardResponse.data
                            val dbAccount = detail.accounts.find { it.accountId == accountId }
                                ?: return@combine SecurityHoldingDetailScreenState.Error("Account not found")
                            val account = Account(
                                accountId = dbAccount.accountId,
                                name = dbAccount.name,
                                officialName = dbAccount.officialName,
                                type = dbAccount.type,
                                subtype = dbAccount.subtype,
                                currentBalance = dbAccount.currentBalance,
                                availableBalance = dbAccount.availableBalance,
                                isoCurrencyCode = dbAccount.isoCurrencyCode,
                                institutionName = dbAccount.institutionName,
                                itemId = dbAccount.itemId,
                                nickname = dbAccount.nickname
                            )

                            val investments = detail.investments ?: (investmentsResponse as? Response.Success)?.data
                            val holding = investments?.holdings?.find { it.accountId == accountId && it.securityId == securityId }
                                ?: return@combine SecurityHoldingDetailScreenState.Error("Holding not found")

                            val allTransactions = investments.transactions.filter { it.accountId == accountId }

                            processData(account, holding, allTransactions, securityPerformance)
                        }
                    }
                }.collect { _state.value = it }
            }
        } else {
            viewModelScope.launch {
                combine(
                    accountUseCase.GetAccounts(),
                    transactionRepository.investments,
                    _securityPerformance
                ) { accountsResponse, investmentsResponse, securityPerformance ->
                    when {
                        accountsResponse is Response.Loading || investmentsResponse is Response.Loading -> {
                            SecurityHoldingDetailScreenState.Loading
                        }
                        accountsResponse is Response.Error -> {
                            SecurityHoldingDetailScreenState.Error(accountsResponse.message)
                        }
                        investmentsResponse is Response.Error -> {
                            SecurityHoldingDetailScreenState.Error(investmentsResponse.message)
                        }
                        accountsResponse is Response.Success && investmentsResponse is Response.Success -> {
                            val account = accountsResponse.data.find { it.accountId == accountId }
                                ?: return@combine SecurityHoldingDetailScreenState.Error("Account not found")

                            val investments = investmentsResponse.data
                            val holding = investments?.holdings?.find { it.accountId == accountId && it.securityId == securityId }
                                ?: return@combine SecurityHoldingDetailScreenState.Error("Holding not found")

                            val allTransactions = investments.transactions.filter { it.accountId == accountId }

                            processData(account, holding, allTransactions, securityPerformance)
                        }
                        else -> SecurityHoldingDetailScreenState.Loading
                    }
                }.collect { _state.value = it }
            }
        }
    }

    private fun processData(
        account: Account,
        holding: Holding,
        allTransactions: List<InvestmentTransaction>,
        securityPerformance: SecurityPerformance?
    ): SecurityHoldingDetailScreenState {
        val ticker = holding.proxySecurity?.tickerSymbol ?: holding.security?.tickerSymbol
        val holdingTransactions = allTransactions.filter { tx ->
            tx.securityId == securityId || 
            (tx.security?.securityId == securityId) ||
            (ticker != null && tx.security?.tickerSymbol == ticker)
        }

        val today = try {
            Clock.System.todayIn(TimeZone.currentSystemDefault())
        } catch (e: Exception) {
            Clock.System.todayIn(TimeZone.currentSystemDefault())
        }
        
        val rawEntries = (if (ticker != null) {
            securityPerformance?.history?.entries?.firstOrNull { it.key.trim().equals(ticker.trim(), ignoreCase = true) }?.value
        } else null) ?: emptyList()
        
        val currentInstVal = holding.institutionValue ?: (holding.quantity * holding.institutionPrice)
        
        val sortedRaw = rawEntries.mapNotNull { entry ->
            try {
                LocalDate.parse(entry.date.substringBefore('T')) to entry.value
            } catch (e: Exception) {
                null
            }
        }.sortedBy { it.first }

        val scaledHistory = if (sortedRaw.isEmpty()) {
            val startDate = today.minus(1, DateTimeUnit.YEAR)
            listOf(startDate to currentInstVal, today to currentInstVal)
        } else {
            val lastRawVal = sortedRaw.last().second
            val scale = if (lastRawVal > 0.0) currentInstVal / lastRawVal else 1.0
            sortedRaw.map { (date, value) -> date to (value * scale) }
        }

        // Reconstruct quantities backwards
        val historyDates = scaledHistory.map { it.first }
        val quantityMap = reconstructDailyQuantities(holding.quantity, holdingTransactions, historyDates, today)

        // Compute price entries
        val priceHistory = scaledHistory.mapNotNull { (date, value) ->
            val qty = quantityMap[date] ?: 0.0
            if (qty > 0.0) {
                val price = value / qty
                SecurityPriceEntry(
                    date = date,
                    price = price,
                    quantity = qty,
                    value = value
                )
            } else {
                null
            }
        }.sortedByDescending { it.date }

        return SecurityHoldingDetailScreenState.Loaded(
            account = account,
            holding = holding,
            transactions = holdingTransactions,
            priceHistory = priceHistory,
            chartPoints = scaledHistory
        )
    }

    private fun reconstructDailyQuantities(
        currentQuantity: Double,
        transactions: List<InvestmentTransaction>,
        historyDates: List<LocalDate>,
        today: LocalDate
    ): Map<LocalDate, Double> {
        val txsByDate = transactions.groupBy {
            try {
                LocalDate.parse(it.date)
            } catch (e: Exception) {
                LocalDate(2000, 1, 1)
            }
        }
        val allDates = (historyDates + today).distinct().sortedDescending()
        
        val quantityMap = mutableMapOf<LocalDate, Double>()
        var runningQuantity = currentQuantity
        
        for (date in allDates) {
            quantityMap[date] = runningQuantity
            
            val dayTxs = txsByDate[date] ?: emptyList()
            for (tx in dayTxs) {
                val q = tx.quantity ?: 0.0
                if (tx.type == "buy") {
                    runningQuantity -= q
                } else if (tx.type == "sell") {
                    runningQuantity += q
                }
            }
        }
        
        return quantityMap
    }
}
